"use strict";
// Copyright © Veeam Software Group GmbH
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataGridNewModel = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const operators_1 = require("rxjs/operators");
const smart_1 = require("../../smart");
class DataGridNewModel extends smart_1.CollectionModel {
    constructor(idGetter, loader, initialValue) {
        super((data) => {
            if (data[this.loadingRowProp]) {
                return this.compoundGroupKey(data[this.loadingRowProp]);
            }
            const group = this.getGroupByData(data);
            if (group) {
                return this.compoundGroupKey(group.key);
            }
            return idGetter(data);
        }, loader, Object.assign(Object.assign({}, initialValue), {
            grouping: [],
            rootGroup: {
                key: [],
                data: {},
                childGroups: [],
                items: [],
                offset: 0,
                totalCount: 0,
                isLoaded: false
            }
        }));
        this.loadingRowProp = Symbol('loadingRowProp');
        this.loaderType = {};
        this.groupedItems = [];
        this.expandedGroupKeys = [];
    }
    compoundGroupKey(groupKey, depth) {
        return groupKey.slice(0, depth).join('/');
    }
    getGroupByData(data) {
        var _a;
        let current;
        const visitors = this.getValue().rootGroup.childGroups.slice().reverse();
        while ((current = visitors.pop())) {
            if (current.data === data) {
                return current;
            }
            visitors.push(...((_a = current.childGroups) !== null && _a !== void 0 ? _a : []).slice().reverse());
        }
        return current !== null && current !== void 0 ? current : null;
    }
    getGroupByKey(key, rootGroup) {
        let result = null;
        if (!key.length) {
            return rootGroup;
        }
        key.reduce((acc, keyValue, idx) => {
            var _a;
            const foundGroup = (_a = acc === null || acc === void 0 ? void 0 : acc.childGroups) === null || _a === void 0 ? void 0 : _a.find((group) => (0, fast_deep_equal_1.default)(group.key, [...acc.key, keyValue]));
            if (key.length === idx + 1) {
                result = foundGroup !== null && foundGroup !== void 0 ? foundGroup : null;
            }
            return foundGroup;
        }, rootGroup);
        return result;
    }
    generateArrayWithNulls(count, group) {
        if (count <= 0) {
            return [];
        }
        return new Array(count).fill({
            data: null,
            isGroup: false,
            isLoading: false,
            group: group
        });
    }
    getGroupValueKeyByParentGroupKey(item, groupKey) {
        var _a, _b;
        const value = this.getValue();
        return (_b = item[(_a = value.grouping[groupKey.length]) === null || _a === void 0 ? void 0 : _a.property]) !== null && _b !== void 0 ? _b : null;
    }
    mapItemToGroup(item, groupKey) {
        return {
            key: [...groupKey, this.getGroupValueKeyByParentGroupKey(item, groupKey)],
            data: Object.assign({}, item),
            offset: 0,
            totalCount: 0,
            childGroups: [],
            items: [],
            isLoaded: false
        };
    }
    processLoaderData(data, payload) {
        this.update((value) => {
            var _a, _b;
            const foundGroup = this.getGroupByKey(payload.groupKey, value.rootGroup);
            if (foundGroup) {
                const isLeavesLoading = this.hasGrouping() &&
                    this.getValue().grouping.length === payload.groupKey.length;
                if (isLeavesLoading) {
                    foundGroup.items = data.items;
                }
                else {
                    foundGroup.childGroups = data.items.map((item) => this.mapItemToGroup(item, payload.groupKey));
                }
                foundGroup.totalCount = (_a = data.totalCount) !== null && _a !== void 0 ? _a : 0;
                foundGroup.offset = (_b = data.prevOffset) !== null && _b !== void 0 ? _b : 0;
                foundGroup.isLoaded = true;
            }
        });
    }
    runLoaderForRoot(prevValue) {
        var _a, _b;
        const payload = { groupKey: [] };
        (_a = this.loadSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = this.syncSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
        this.updateMeta((prevMeta) => {
            prevMeta.loading = true;
            prevMeta.outdated = false;
            prevMeta.syncing = false;
        });
        this.nextData = null;
        this.nextExtraMeta = null;
        return new Promise((resolve, reject) => {
            this.loadSubscription = this.loader(prevValue, payload)
                .pipe((0, operators_1.take)(1))
                .subscribe({
                next: ({ data, meta }) => {
                    this.loadSubscription = null;
                    try {
                        this.processLoaderData(data, payload);
                        this.updateMeta((prevMeta) => {
                            prevMeta.done = true;
                            prevMeta.loading = false;
                            prevMeta.error = null;
                            if (meta) {
                                prevMeta.extra = meta;
                            }
                        });
                    }
                    catch (err) {
                        reject(err);
                    }
                },
                error: (error) => {
                    this.loadSubscription = null;
                    try {
                        this.updateMeta((prevMeta) => {
                            prevMeta.done = true;
                            prevMeta.loading = false;
                            prevMeta.error = error;
                        });
                        resolve();
                    }
                    catch (err) {
                        reject(err);
                    }
                },
                complete: () => {
                    resolve();
                    this.loadSubscription = null;
                }
            });
        });
    }
    runLoader(prevValue, payload) {
        return new Promise((resolve, reject) => {
            this.loadSubscription = this.loader(prevValue, payload)
                .pipe((0, operators_1.take)(1))
                .subscribe({
                next: ({ data }) => {
                    try {
                        this.processLoaderData(data, payload);
                    }
                    catch (err) {
                        reject(err);
                    }
                },
                error: (error) => {
                    reject(error);
                },
                complete: () => {
                    resolve();
                }
            });
        });
    }
    loadGroups(groupKeys) {
        return __awaiter(this, void 0, void 0, function* () {
            if (groupKeys.length === 1 && groupKeys[0].length === 0) {
                yield this.runLoaderForRoot(this.getValue());
            }
            else {
                yield Promise.all(groupKeys.map((groupKey) => __awaiter(this, void 0, void 0, function* () {
                    yield this.runLoader(this.getValue(), { groupKey });
                })));
            }
            this.processGroupedItemsByGroups();
        });
    }
    clearGroups() {
        this.expandedGroupKeys = [];
        this.update((value) => {
            value.rootGroup = {
                key: [],
                data: {},
                childGroups: [],
                items: [],
                offset: 0,
                totalCount: 0,
                isLoaded: false
            };
        });
    }
    processGroupedItemsByGroups() {
        var _a, _b, _c, _d, _e;
        this.groupedItems = [];
        const value = this.getValue();
        const offsetItems = (_b = (_a = value.pagination) === null || _a === void 0 ? void 0 : _a.offset) !== null && _b !== void 0 ? _b : 0;
        const limitItems = (_d = (_c = value.pagination) === null || _c === void 0 ? void 0 : _c.limit) !== null && _d !== void 0 ? _d : Infinity;
        let totalCount = value.rootGroup.totalCount;
        let currentGroup;
        const visitors = value.rootGroup.childGroups.slice().reverse();
        while ((currentGroup = visitors.pop())) {
            this.groupedItems.push({
                data: currentGroup.data,
                isGroup: true,
                isLoading: false,
                group: currentGroup
            });
            if (this.isGroupExpanded(currentGroup.key)) {
                if (!currentGroup.isLoaded) {
                    this.groupedItems.push({
                        group: currentGroup !== null && currentGroup !== void 0 ? currentGroup : value.rootGroup,
                        data: { [this.loadingRowProp]: [...currentGroup.key, 'loading'] },
                        isGroup: false,
                        isLoading: true
                    });
                    totalCount += 1;
                    continue;
                }
                if (currentGroup.key.length === value.grouping.length) {
                    this.groupedItems.push(...this.generateArrayWithNulls(Math.max(currentGroup.offset, 0), currentGroup), ...currentGroup.items.map((item) => ({
                        group: currentGroup !== null && currentGroup !== void 0 ? currentGroup : value.rootGroup,
                        data: item,
                        isGroup: false,
                        isLoading: false
                    })), ...this.generateArrayWithNulls(Math.max(currentGroup.totalCount -
                        currentGroup.offset -
                        currentGroup.items.length, 0), currentGroup));
                }
                totalCount += currentGroup.totalCount;
                visitors.push(...((_e = currentGroup.childGroups) !== null && _e !== void 0 ? _e : []).slice().reverse());
            }
        }
        this.groupedItems.splice(0, offsetItems);
        this.groupedItems.splice(limitItems, Infinity);
        this.update((value) => {
            value.items = this.groupedItems.map(({ data }) => data);
            value.totalCount = totalCount;
            value.prevOffset = offsetItems;
        });
    }
    withAdditionalValues(additionalValues) {
        this.update((prevValue) => {
            const keys = Object.keys(additionalValues);
            keys.forEach((key) => {
                prevValue[key] = additionalValues[key];
            });
        });
        return this;
    }
    hasGrouping() {
        return this.getValue().grouping.length > 0;
    }
    hasGroups() {
        return this.getValue().rootGroup.childGroups.length > 0;
    }
    withGrouping(grouping) {
        this.update((prevValue) => {
            prevValue.grouping = Array.isArray(grouping) ? grouping : [grouping];
        });
        return this;
    }
    load() {
        const _super = Object.create(null, {
            load: { get: () => super.load }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.hasGrouping()) {
                yield _super.load.call(this);
                if (this.hasGroups()) {
                    this.clearGroups();
                }
                return;
            }
            this.expandedGroupKeys = [];
            yield this.loadGroups([[]]);
        });
    }
    isGroupExpanded(groupKey) {
        if (!groupKey.length) {
            return false;
        }
        return !!this.expandedGroupKeys.find((expandedGroupKey) => (0, fast_deep_equal_1.default)(expandedGroupKey, groupKey));
    }
    toggleExpandedGroups(groupKeys) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!groupKeys.length) {
                return;
            }
            const groupKeysToExpand = [];
            const compoundGroupKeysToCollapse = [];
            groupKeys.forEach((groupKey) => {
                if (this.isGroupExpanded(groupKey)) {
                    compoundGroupKeysToCollapse.push(groupKey);
                }
                else {
                    groupKeysToExpand.push(groupKey);
                }
            });
            this.expandedGroupKeys = this.expandedGroupKeys
                .filter((groupKey) => !compoundGroupKeysToCollapse.some((collapsedGroupKey) => this.compoundGroupKey(collapsedGroupKey) ===
                this.compoundGroupKey(groupKey, collapsedGroupKey.length)))
                .concat(groupKeysToExpand);
            this.processGroupedItemsByGroups();
            const groupKeysForLoad = groupKeysToExpand.filter((groupKey) => { var _a; return !((_a = this.getGroupByKey(groupKey, this.getValue().rootGroup)) === null || _a === void 0 ? void 0 : _a.isLoaded); });
            if (groupKeysForLoad.length) {
                yield this.loadGroups(groupKeysForLoad);
            }
        });
    }
    getGroupedItemByIndex(index) {
        var _a;
        return (_a = this.groupedItems[index]) !== null && _a !== void 0 ? _a : null;
    }
}
exports.DataGridNewModel = DataGridNewModel;
//# sourceMappingURL=DataGridNewModel.js.map